import * as Sentry from '@sentry/react';

const BASE = '/api';

export async function login(email, password) {
  const res = await fetch(BASE + '/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  });
  if (!res.ok) throw new Error('Invalid email or password');
  const data = await res.json();
  localStorage.setItem('tg_token', data.token);
  localStorage.setItem('tg_user', JSON.stringify(data.user));
  // Follow the user's saved UI language: persist it and notify i18n so the app
  // switches before the first authenticated screen renders.
  if (data.user.preferred_language) {
    localStorage.setItem('tg_lang', data.user.preferred_language);
    window.dispatchEvent(new Event('tg_lang_change'));
  }
  // Tell Sentry who is logged in so error reports include the affected user.
  Sentry.setUser({ id: String(data.user.id), email: data.user.email, org: data.user.org_name });
  return data;
}

export function logout() {
  localStorage.removeItem('tg_token');
  localStorage.removeItem('tg_user');
  Sentry.setUser(null); // clear user context on logout
  window.location.href = '/';
}

export function getToken() {
  return localStorage.getItem('tg_token');
}

export function getUser() {
  try {
    return JSON.parse(localStorage.getItem('tg_user'));
  } catch {
    return null;
  }
}

export function isLoggedIn() {
  return !!getToken();
}

// ── Impersonation ───────────────────────────────────────────────────────────
// A superadmin can open the app with ?impersonate=TOKEN to view it as a customer.
// The token is a normal JWT carrying the target's org + an `impersonated_by`
// claim. We swap it in as the active session (saving the prior one so it can be
// restored), strip it from the URL, and surface a banner gated on that claim.

const ADMIN_URL = import.meta.env.VITE_ADMIN_URL || 'https://admin.tankguard.de';

// Decode a JWT payload (no verification — the server verifies on every call).
export function decodeToken(token) {
  try {
    const part = token.split('.')[1];
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch { return null; }
}

// Runs once at startup (main.jsx) before anything reads the token. Returns true
// if an impersonation token was applied.
export function applyImpersonationFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const tok = params.get('impersonate');
  if (!tok) return false;
  // Preserve the operator's own app session (if any) so "End" can restore it.
  const prevToken = localStorage.getItem('tg_token');
  if (prevToken && prevToken !== tok) {
    localStorage.setItem('tg_token_prev', prevToken);
    const prevUser = localStorage.getItem('tg_user');
    if (prevUser) localStorage.setItem('tg_user_prev', prevUser);
  }
  localStorage.setItem('tg_token', tok);
  const c = decodeToken(tok);
  if (c) {
    localStorage.setItem('tg_user', JSON.stringify({
      id: c.id, email: c.email, role: c.role, org_id: c.org_id,
      name: c.name, org_name: c.org_name,
    }));
  }
  // Strip the token from the URL so a refresh or shared link can't replay it.
  params.delete('impersonate');
  const qs = params.toString();
  window.history.replaceState({}, '', window.location.pathname + (qs ? '?' + qs : ''));
  return true;
}

// Returns { name, org_name, email } when the active token is an impersonation
// session, else null. Drives the amber banner.
export function getImpersonation() {
  const t = getToken();
  if (!t) return null;
  const c = decodeToken(t);
  if (!c || !c.impersonated_by) return null;
  return { name: c.name || c.email, org_name: c.org_name, email: c.email };
}

// End impersonation: log it server-side (best-effort), then restore the prior
// admin/app session if one was saved, otherwise bounce back to the admin console.
export async function endImpersonation() {
  try { await apiFetch('/admin/impersonate/end', { method: 'POST' }); } catch { /* token may already be expired */ }
  const prev = localStorage.getItem('tg_token_prev');
  const prevUser = localStorage.getItem('tg_user_prev');
  localStorage.removeItem('tg_token_prev');
  localStorage.removeItem('tg_user_prev');
  if (prev) {
    localStorage.setItem('tg_token', prev);
    if (prevUser) localStorage.setItem('tg_user', prevUser);
    window.location.href = '/';
  } else {
    localStorage.removeItem('tg_token');
    localStorage.removeItem('tg_user');
    window.location.href = ADMIN_URL;
  }
}

// Shared authenticated fetch for all /api/ routes.
// Returns parsed JSON, or null + redirects to / on 401.
export async function apiFetch(path, opts = {}) {
  const token = getToken();
  const res = await fetch(BASE + path, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + (token || ''),
      ...(opts.headers || {})
    }
  });
  if (res.status === 401) {
    if (token) logout(); // only redirect if we had an active session
    return null;
  }
  if (!res.ok) throw new Error('Request failed: ' + res.status);
  return res.json();
}
